import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const LAST_UPDATED = "February 10, 2026";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-black/5">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-home-link">
            <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold tracking-tight text-charcoal-900 text-[15px]">Mission Watch</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-charcoal-900">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14" data-testid="privacy-page">
        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Legal</p>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-charcoal-900 mt-2">Privacy Policy</h1>
        <p className="text-sm text-charcoal-500 mt-3 font-mono">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-charcoal mt-10 max-w-none">
          <Section title="1. Who we are">
            <p>Mission Watch (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) provides monthly operations checkups for small nonprofits. This Privacy Policy explains how we collect, use, and protect the information we handle when you use missionwatch.app and related services.</p>
          </Section>

          <Section title="2. Information we collect">
            <ul>
              <li><b>Account information:</b> your name, email address, and (hashed) password.</li>
              <li><b>Organization details you provide:</b> organization name, website URL, donation page URL, contact page URL, social links, and any custom links you ask us to monitor.</li>
              <li><b>Team members you invite:</b> email addresses of teammates you add to receive reports.</li>
              <li><b>Report data:</b> results of the automated checks we run against your public website (uptime, SSL status, page load, broken links, form detection, etc.).</li>
              <li><b>Billing data:</b> when you subscribe, our payment processor (Stripe) collects your card details directly. We never see or store your full card number.</li>
              <li><b>Usage &amp; log data:</b> IP address, browser type, pages viewed, and timestamps for security and product improvement.</li>
            </ul>
          </Section>

          <Section title="3. How we use your information">
            <ul>
              <li>To run monitoring checks against the URLs you tell us to watch, and to generate your Mission Watch Score.</li>
              <li>To email you monthly reports, instant issue alerts, and product notifications.</li>
              <li>To process subscription payments via Stripe.</li>
              <li>To generate AI-powered executive summaries and recommendations for your reports.</li>
              <li>To secure the service, prevent abuse, and comply with legal obligations.</li>
            </ul>
          </Section>

          <Section title="4. Automated monitoring">
            <p>Mission Watch fetches your public web pages (like a search-engine crawler would) to check availability, security, and quality. We identify our crawler in the <span className="font-mono">User-Agent</span> string and respect standard rate limits. We do not attempt to access non-public content, log in to systems on your behalf, or bypass access controls.</p>
          </Section>

          <Section title="5. AI processing">
            <p>Your Mission Watch Score reports include an AI-generated executive summary. We send the report&rsquo;s check results (not your account credentials or full URLs of gated content) to a large language model provider under a contract that prohibits training on your data. AI output is provided for guidance and may be imperfect.</p>
          </Section>

          <Section title="6. Third parties we use">
            <ul>
              <li><b>Stripe</b> — payment processing (subject to Stripe&rsquo;s privacy policy).</li>
              <li><b>Resend</b> — transactional email delivery.</li>
              <li><b>MongoDB Atlas</b> — encrypted data storage.</li>
              <li><b>OpenAI</b> — AI summary generation (no training on your data).</li>
            </ul>
            <p>We do not sell or rent your personal information to anyone. Ever.</p>
          </Section>

          <Section title="7. Data retention">
            <p>We keep your account and report history for as long as your account is active. If you cancel, we retain limited billing records as required by law and delete monitoring data on request. Email <a href="mailto:hello@missionwatch.app" className="text-brand hover:underline">hello@missionwatch.app</a> to request deletion.</p>
          </Section>

          <Section title="8. Security">
            <p>Passwords are hashed with bcrypt. Data is transmitted over TLS. Access to production systems is restricted and logged. No system is perfectly secure — if you suspect an issue, please email us right away.</p>
          </Section>

          <Section title="9. Your rights">
            <p>Depending on where you live, you may have the right to access, correct, export, or delete your personal information. To exercise any of these rights, email <a href="mailto:hello@missionwatch.app" className="text-brand hover:underline">hello@missionwatch.app</a> and we&rsquo;ll respond within 30 days.</p>
          </Section>

          <Section title="10. Children">
            <p>Mission Watch is not directed to children under 16. We do not knowingly collect personal information from children.</p>
          </Section>

          <Section title="11. Changes to this policy">
            <p>If we make material changes, we&rsquo;ll email account owners and update the &ldquo;Last updated&rdquo; date above. Continued use of the service after changes means you accept the updated policy.</p>
          </Section>

          <Section title="12. Contact us">
            <p>Questions? Email <a href="mailto:hello@missionwatch.app" className="text-brand hover:underline">hello@missionwatch.app</a>.</p>
          </Section>
        </div>

        <div className="mt-14 pt-6 border-t border-black/5 flex items-center justify-between text-sm">
          <Link to="/" className="text-charcoal-500 hover:text-charcoal-900">← Back to home</Link>
          <Link to="/terms" className="text-brand hover:underline" data-testid="privacy-to-terms">Read Terms of Service →</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold text-charcoal-900 mb-3">{title}</h2>
      <div className="text-base text-charcoal-600 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}
